import Decimal from 'decimal.js';

import { headerIndex } from './lookupSheet';
import { toCsv, type UploadLine } from './resultsToUpload';
import {
  as400Date,
  cellDate,
  cellDecimal,
  cellText,
  columnIndex,
  isBlankRow,
  isErrorCell,
  type ResultsCell,
  type ResultsSheet,
} from './sheetCells';

/**
 * Rockwell corporate SPAs → the AS400 Header Cross upload CSV.
 *
 * Two inputs: the ALL FIXED SAPS export (one row per catalog- or price-group
 * rule of each corporate agreement, the customer named in `Cust Name`) and a
 * Rockwell Price Book Template (every catalog number with its list price and
 * PGC). Only the template's Price Book sheet is read (Catalog Number, AS400,
 * List Price, PGC); its Rules Summary sheet is ignored, since every rate comes
 * from ALL FIXED SAPS (Rate, Customer Rate):
 *
 * - A catalog rule (`Catalog No` filled) wins; else the price-group rule for
 *   the item's PGC (`Catalog No` blank, `Price Group` filled).
 * - Sell (V) = ROUND(List × (100 + Customer Rate) / 100, 2).
 * - Cost (W) = ROUND(List × (100 + Rate) / 100, 4).
 *
 * Rates are SAP-signed: `66.200-` is −66.2 and `-71.900` is −71.9. Every
 * price-book item a rule prices is one upload line, in price-book order,
 * except an item with no AS400 code (blank or `#N/A`), which AS400 can't
 * load, and one whose margin (Sell − Cost) / Sell is 0 or less.
 *
 * Each SPA is priced from the price book in effect on its `Fixed val.date`
 * (a fixed SAP): the dropped book whose file-name dates cover that day. One
 * upload is one division, its SPAs each priced from their own book.
 *
 * Open with the pricing team: a price-group rule prices every item in its
 * group, so one load can run to ~100k lines (Cargill, Mars, ExxonMobil) and a
 * division past 250k. Confirm AS400 wants one line per item there, not one
 * line per price group.
 */

/** The reference upload stops at column BK (ROUNDUP): 63 fields a line. */
export const CORPORATE_UPLOAD_WIDTH = columnIndex('BK') + 1;

/** SAPS DIVISION → AS400 branch: `+` is W+, `B` is WB, `J` is WJ. */
const BRANCH_DIVISIONS = new Set(['+', 'B', 'J']);
const LINE2_WIDTH = 20;
const LINE2_SUFFIX = 'N.D.';

/**
 * LINE2: the branch and `NAT`, then `N.D.` as the last characters of 20 —
 * `W+ NAT          N.D.`.
 */
export function corporateLine2(division: string): string {
  const start = `W${division} NAT`;
  return `${start.padEnd(LINE2_WIDTH - LINE2_SUFFIX.length)}${LINE2_SUFFIX}`;
}

/** AS400's TITLE field holds 20 characters. */
const TITLE_WIDTH = 20;

/** TITLE (column C): the Cust Name upper-cased, cut to AS400's 20 characters. */
export function corporateTitle(customer: string): string {
  return customer.toUpperCase().slice(0, TITLE_WIDTH).trimEnd();
}

export const SAPS_HEADERS = {
  division: 'DIVISION',
  customer: 'Cust Name',
  loadedTo: 'LOADED TO',
  oldSpa: 'OLD SPA',
  newSpa: 'NEW SPA',
  start: 'START',
  end: 'END',
  fixedDate: 'Fixed val.date',
  catalog: 'Catalog No',
  priceGroup: 'Price Group',
  rate: 'Rate',
  customerRate: 'Customer Rate',
} as const;

export const PRICE_BOOK_HEADERS = {
  catalog: 'Catalog Number',
  item: 'AS400',
  listPrice: 'List Price',
  priceGroup: 'PGC',
} as const;

/** A header's column: every wanted header is known to be present. */
type ColumnOf<K extends string> = (key: K) => number;

/** The sheet's column for each wanted header, or the headers it lacks. */
function resolveHeaders<K extends string>(
  sheet: ResultsSheet,
  wanted: Record<K, string>,
): { columns: ColumnOf<K> } | { missing: string[] } {
  const index = headerIndex(sheet.headers);
  const missing = Object.values<string>(wanted).filter(
    (header) => !index.has(header),
  );
  if (missing.length > 0) return { missing };
  return { columns: (key) => index.get(wanted[key]) ?? -1 };
}

export type CorporateFileKind = 'saps' | 'priceBook';

/**
 * Which kind of file a dropped workbook is, from its header row — or, when it
 * is neither, the kind it comes closest to and the columns that kind lacks.
 * `closest` is null when it is not even half of either: an unrelated file.
 */
export function classifyCorporateFile(
  sheet: ResultsSheet,
):
  | { kind: CorporateFileKind }
  | { kind: null; closest: CorporateFileKind | null; missing: string[] } {
  const saps = resolveHeaders(sheet, SAPS_HEADERS);
  if ('columns' in saps) return { kind: 'saps' };
  const priceBook = resolveHeaders(sheet, PRICE_BOOK_HEADERS);
  if ('columns' in priceBook) return { kind: 'priceBook' };
  // Share of each kind's columns the file has; the higher one is meant.
  const sapsShare = 1 - saps.missing.length / Object.keys(SAPS_HEADERS).length;
  const bookShare =
    1 - priceBook.missing.length / Object.keys(PRICE_BOOK_HEADERS).length;
  if (Math.max(sapsShare, bookShare) < 0.5) {
    return { kind: null, closest: null, missing: [] };
  }
  return sapsShare >= bookShare
    ? { kind: null, closest: 'saps', missing: saps.missing }
    : { kind: null, closest: 'priceBook', missing: priceBook.missing };
}

/** Which kind of file a dropped workbook is, from the headers it carries. */
export function corporateFileKind(
  sheet: ResultsSheet,
): CorporateFileKind | null {
  return classifyCorporateFile(sheet).kind;
}

/** Why a dropped file was not taken. */
export type RejectedFile =
  | { fileName: string; reason: 'unreadable'; message: string }
  | {
      fileName: string;
      reason: 'missing-columns';
      closest: CorporateFileKind;
      missing: string[];
      /** The header row read, so a shifted or renamed header is visible. */
      headerRowNumber: number;
    }
  /** Neither layout: not half the columns of either. */
  | {
      fileName: string;
      reason: 'unrecognised';
      headerRowNumber: number;
      /** The first few headers found, to show what the file is. */
      headers: string[];
    };

/** Headers an unrecognised file's message shows. */
const HEADERS_SHOWN = 6;

/** Why a read workbook is not taken, from its classification. */
export function rejectionFor(
  fileName: string,
  sheet: ResultsSheet,
  found: Extract<ReturnType<typeof classifyCorporateFile>, { kind: null }>,
): RejectedFile {
  const headerRowNumber = sheet.headerRowNumber ?? 1;
  if (found.closest === null) {
    const headers = sheet.headers
      .flatMap((header) => (header.trim() === '' ? [] : [header.trim()]))
      .slice(0, HEADERS_SHOWN);
    return { fileName, reason: 'unrecognised', headerRowNumber, headers };
  }
  return {
    fileName,
    reason: 'missing-columns',
    closest: found.closest,
    missing: found.missing,
    headerRowNumber,
  };
}

/** A SAP rate: `66.200-` (trailing minus) is −66.2; plain decimals as typed. */
export function sapRate(cell: ResultsCell): Decimal | null {
  if (typeof cell === 'number') return cellDecimal(cell);
  const text = cellText(cell);
  return text.endsWith('-')
    ? cellDecimal(`-${text.slice(0, -1).trim()}`)
    : cellDecimal(text);
}

/** Rule and item keys compare as Excel's VLOOKUP does: trimmed, any case. */
function ruleKey(cell: ResultsCell): string {
  return cellText(cell).toUpperCase();
}

/**
 * One load: a customer's rows for one LOADED TO in one DIVISION. Every load
 * of a division goes in that division's upload; divisions never mix.
 */
export interface CorporateLoad {
  /** `Cust Name`, which is also the load's AS400 TITLE. */
  customer: string;
  /** `LOADED TO`: `A-15802` (account) or `G-SUE` (group). */
  loadTo: string;
  /** `DIVISION`: `+`, `B` or `J`. */
  division: string;
}

function loadKey({ customer, loadTo, division }: CorporateLoad): string {
  return JSON.stringify([customer, loadTo, division]);
}

/** The named loads of a SAPS sheet, first seen first. */
export function corporateLoads(saps: ResultsSheet): CorporateLoad[] {
  const resolved = resolveHeaders(saps, SAPS_HEADERS);
  if (!('columns' in resolved)) return [];
  const { columns } = resolved;
  const loads = new Map<string, CorporateLoad>();
  for (const row of saps.rows) {
    const load = {
      customer: cellText(row[columns('customer')]),
      loadTo: cellText(row[columns('loadedTo')]),
      division: cellText(row[columns('division')]),
    };
    if (load.customer === '') continue;
    const key = loadKey(load);
    if (!loads.has(key)) loads.set(key, load);
  }
  return [...loads.values()];
}

/** The divisions the loads fall in, first seen first. */
export function corporateDivisions(loads: CorporateLoad[]): string[] {
  return [...new Set(loads.map(({ division }) => division))];
}

/** `A-15802` loads under account 15802; `G-SUE` under customer group SUE. */
export function parseLoadTarget(
  target: string,
): { kind: 'account' | 'group'; code: string } | null {
  const match = /^([AG])-(.+)$/i.exec(target.trim());
  if (!match) return null;
  const [, kind = '', code = ''] = match;
  return { kind: kind.toUpperCase() === 'A' ? 'account' : 'group', code };
}

interface Rates {
  rate: Decimal;
  customerRate: Decimal;
}

export interface CorporateAgreement {
  customer: string;
  loadTo: string;
  /** SAPS DIVISION: `+`, `B` or `J`. */
  division: string;
  oldSpa: string;
  newSpa: string;
  start: ResultsCell;
  end: ResultsCell;
  /** `Fixed val.date`: the day whose price book prices this SPA. */
  fixedDate: ResultsCell;
  /** Catalog number (upper-cased) → its rates. */
  catalogRules: Map<string, Rates>;
  /** PGC (upper-cased) → its rates. */
  priceGroupRules: Map<string, Rates>;
}

export type CorporateProblem =
  | { kind: 'missing-headers'; file: 'saps' | 'priceBook'; headers: string[] }
  | { kind: 'no-rows'; customer: string; loadTo: string }
  | { kind: 'bad-load-target'; loadTo: string }
  /** DIVISION is not one of the three branches (`+`, `B`, `J`). */
  | { kind: 'bad-division'; value: string }
  /** Rows of one agreement disagree on a header field (OLD SPA, START…). */
  | { kind: 'mixed-agreement'; header: string; values: string[] }
  | { kind: 'bad-date'; header: string; value: string }
  | { kind: 'bad-rate'; sheetRow: number; header: string; value: string }
  | { kind: 'no-rule-key'; sheetRow: number }
  /** One catalog number or price group with two different sets of rates. */
  | { kind: 'conflicting-rule'; sheetRow: number; key: string }
  /** An Excel error (`#N/A`…) in a field every line carries: AS400 rejects it. */
  | { kind: 'error-value'; header: string; value: string }
  /** More than one dropped price book covers the SPA's Fixed val.date. */
  | { kind: 'ambiguous-price-book'; day: string; priceBookNames: string[] };

/**
 * An Excel error as text: `#N/A`, `#NAME?`, or any `#WORD!` (`#REF!`,
 * `#VALUE!`, `#FIELD!`, `#BLOCKED!`, and whatever Excel adds next), plus the
 * reader's own `#UNCALCULATED`/`#ERROR`. AS400 rejects an upload holding one,
 * so none may reach UPLOAD.csv.
 */
export function isExcelErrorText(value: string): boolean {
  return /^#(N\/A|GETTING_DATA|UNCALCULATED|ERROR|[A-Z][A-Z0-9/_]*[!?])$/i.test(
    value.trim(),
  );
}

function sheetRowOf(sheet: ResultsSheet, index: number): number {
  return sheet.rowNumbers?.[index] ?? (sheet.headerRowNumber ?? 1) + 1 + index;
}

function sameRates(a: Rates, b: Rates): boolean {
  return a.rate.equals(b.rate) && a.customerRate.equals(b.customerRate);
}

/** The one value every row gives a header field, or the values they disagree on. */
function singleValue(
  rows: ResultsCell[][],
  column: number,
): { value: ResultsCell } | { values: string[] } {
  const values = [...new Set(rows.map((row) => cellText(row[column])))];
  return values.length === 1
    ? { value: rows[0]?.[column] ?? null }
    : { values };
}

type SapsColumns = ColumnOf<keyof typeof SAPS_HEADERS>;

/** Adds one SAPS row's rule, or says why it can't be one. */
function addRule(
  agreement: CorporateAgreement,
  row: ResultsCell[],
  sheetRow: number,
  columns: SapsColumns,
): CorporateProblem | null {
  const rate = sapRate(row[columns('rate')]);
  const customerRate = sapRate(row[columns('customerRate')]);
  if (!rate || !customerRate) {
    const header = rate ? SAPS_HEADERS.customerRate : SAPS_HEADERS.rate;
    const column = rate ? columns('customerRate') : columns('rate');
    return { kind: 'bad-rate', sheetRow, header, value: cellText(row[column]) };
  }
  const catalog = ruleKey(row[columns('catalog')]);
  const priceGroup = ruleKey(row[columns('priceGroup')]);
  // A catalog row names its price group too; only a row without a catalog
  // number is a price-group rule.
  const [rules, key] =
    catalog !== ''
      ? [agreement.catalogRules, catalog]
      : [agreement.priceGroupRules, priceGroup];
  if (key === '') return { kind: 'no-rule-key', sheetRow };
  const rates = { rate, customerRate };
  const existing = rules.get(key);
  // Rows repeat per load target; a second, different rate is never guessed.
  if (existing && !sameRates(existing, rates)) {
    return { kind: 'conflicting-rule', sheetRow, key };
  }
  rules.set(key, rates);
  return null;
}

type HeaderField =
  'division' | 'oldSpa' | 'newSpa' | 'start' | 'end' | 'fixedDate';

type AgreementHeader = Partial<Record<HeaderField, ResultsCell>>;

const HEADER_FIELDS: HeaderField[] = [
  'division',
  'oldSpa',
  'newSpa',
  'start',
  'end',
  'fixedDate',
];

/** Problems with the values themselves: unreadable dates, unknown division. */
function headerValueProblems(
  header: AgreementHeader,
  date1904: boolean,
): CorporateProblem[] {
  const dates: [HeaderField, (value: ResultsCell) => boolean][] = [
    ['start', (value) => as400Date(value, date1904) !== null],
    ['end', (value) => as400Date(value, date1904) !== null],
    ['fixedDate', (value) => sapDay(value, date1904) !== null],
  ];
  const problems: CorporateProblem[] = dates.flatMap(([key, reads]) => {
    const value = header[key];
    return value === undefined || reads(value)
      ? []
      : [
          {
            kind: 'bad-date',
            header: SAPS_HEADERS[key],
            value: cellText(value),
          },
        ];
  });
  const division = header.division;
  if (division !== undefined && !BRANCH_DIVISIONS.has(cellText(division))) {
    problems.push({ kind: 'bad-division', value: cellText(division) });
  }
  for (const key of ['oldSpa', 'newSpa'] as const) {
    const value = cellText(header[key]);
    if (isExcelErrorText(value)) {
      problems.push({ kind: 'error-value', header: SAPS_HEADERS[key], value });
    }
  }
  return problems;
}

/** The agreement fields every row must share (DIVISION, OLD SPA, dates), checked. */
function agreementHeader(
  rows: ResultsCell[][],
  columns: SapsColumns,
  date1904: boolean,
): { header: AgreementHeader; problems: CorporateProblem[] } {
  const problems: CorporateProblem[] = [];
  const header: AgreementHeader = {};
  for (const key of HEADER_FIELDS) {
    const single = singleValue(rows, columns(key));
    if ('values' in single) {
      problems.push({
        kind: 'mixed-agreement',
        header: SAPS_HEADERS[key],
        values: single.values,
      });
    } else header[key] = single.value;
  }
  problems.push(...headerValueProblems(header, date1904));
  return { header, problems };
}

/** One load's agreement, from its SAPS rows. */
export function readCorporateAgreement(
  saps: ResultsSheet,
  load: CorporateLoad,
): { agreement: CorporateAgreement } | { problems: CorporateProblem[] } {
  const { customer, loadTo } = load;
  const resolved = resolveHeaders(saps, SAPS_HEADERS);
  if (!('columns' in resolved)) {
    return {
      problems: [
        { kind: 'missing-headers', file: 'saps', headers: resolved.missing },
      ],
    };
  }
  const { columns } = resolved;
  if (!parseLoadTarget(loadTo)) {
    return { problems: [{ kind: 'bad-load-target', loadTo }] };
  }
  if (isExcelErrorText(customer)) {
    const header = SAPS_HEADERS.customer;
    return { problems: [{ kind: 'error-value', header, value: customer }] };
  }
  const picked = saps.rows.flatMap((row, index) =>
    !isBlankRow(row) &&
    cellText(row[columns('customer')]) === customer &&
    cellText(row[columns('loadedTo')]) === loadTo &&
    cellText(row[columns('division')]) === load.division
      ? [{ row, sheetRow: sheetRowOf(saps, index) }]
      : [],
  );
  if (picked.length === 0) {
    return { problems: [{ kind: 'no-rows', customer, loadTo }] };
  }

  const { header, problems } = agreementHeader(
    picked.map(({ row }) => row),
    columns,
    saps.date1904 ?? false,
  );
  const agreement: CorporateAgreement = {
    customer,
    loadTo,
    division: cellText(header.division),
    oldSpa: cellText(header.oldSpa),
    newSpa: cellText(header.newSpa),
    start: header.start ?? null,
    end: header.end ?? null,
    fixedDate: header.fixedDate ?? null,
    catalogRules: new Map(),
    priceGroupRules: new Map(),
  };
  for (const { row, sheetRow } of picked) {
    const problem = addRule(agreement, row, sheetRow, columns);
    if (problem) problems.push(problem);
  }
  return problems.length > 0 ? { problems } : { agreement };
}

/**
 * A SAPS day: `YYYY-MM-DD` text or a date serial, or SAP's `MM/DD/YYYY` text
 * (`Fixed val.date` is `10/01/2026`).
 */
function sapDay(cell: ResultsCell, date1904: boolean): Date | null {
  const date = cellDate(cell, date1904);
  if (date) return date;
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(cellText(cell));
  if (!match) return null;
  const [month, day, year] = match.slice(1).map(Number);
  if (month === undefined || day === undefined || year === undefined) {
    return null;
  }
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day
    ? parsed
    : null;
}

/** The day whose price book prices the SPA: its `Fixed val.date`. */
export function agreementPricingDay(
  agreement: CorporateAgreement,
  date1904: boolean,
): Date | null {
  return sapDay(agreement.fixedDate, date1904);
}

/**
 * The dates a price book template covers, from its file name:
 * `15. Rockwell Price Book Template 9-5-2026 to 10-3-2026.xlsx`.
 */
export function priceBookWindow(
  fileName: string,
): { from: Date; to: Date } | null {
  const match =
    /(\d{1,2})-(\d{1,2})-(\d{4})\s+to\s+(\d{1,2})-(\d{1,2})-(\d{4})/i.exec(
      fileName,
    );
  if (!match) return null;
  const [m1, d1, y1, m2, d2, y2] = match.slice(1).map(Number);
  if ([m1, d1, y1, m2, d2, y2].some((part) => part === undefined)) return null;
  return {
    from: new Date(Date.UTC(y1 ?? 0, (m1 ?? 1) - 1, d1)),
    to: new Date(Date.UTC(y2 ?? 0, (m2 ?? 1) - 1, d2)),
  };
}

/** Whether a price book's file-name dates hold `day`. */
export function priceBookCovers(fileName: string, day: Date): boolean {
  const window = priceBookWindow(fileName);
  return window !== null && window.from <= day && day <= window.to;
}

/** Why a priced item is not in the upload. */
export type LeftOutReason =
  /** No AS400 item to load it under (blank, or an error such as `#N/A`). */
  | 'no-item'
  /** Sell ≤ Cost: a margin of 0 or less. */
  | 'margin'
  /** Some column still holds an Excel error (`#N/A`…), which AS400 rejects. */
  | 'error-value';

export interface LeftOutItem {
  sheetRow: number;
  catalog: string;
  reason: LeftOutReason;
  /** The computed prices, for a margin left-out; blank otherwise. */
  sell: string;
  cost: string;
}

/** One load priced from a price book; LOAD NUM is still blank. */
export type CorporateUploadResult =
  | {
      status: 'ok';
      lines: UploadLine[];
      /** Price-book items no rule prices. */
      unmatched: number;
      /** Items a rule prices whose list price is not a number: left out. */
      unpriced: number;
      leftOut: LeftOutItem[];
    }
  | { status: 'invalid'; problems: CorporateProblem[] };

/** The columns every line of one agreement shares. */
function headerCells(
  agreement: CorporateAgreement,
  date1904: boolean,
): string[] {
  // Only the 63 written fields: a division can run to 250k+ lines.
  const cells = Array.from({ length: CORPORATE_UPLOAD_WIDTH }, () => '');
  const set = (column: string, value: string) => {
    cells[columnIndex(column)] = value;
  };
  set('B', 'C');
  set('C', corporateTitle(agreement.customer));
  set('D', corporateLine2(agreement.division));
  set('E', 'Y');
  set('F', 'ALB * S&D');
  set('G', 'Y');
  set('H', as400Date(agreement.start, date1904) ?? '');
  set('I', as400Date(agreement.end, date1904) ?? '');
  const target = parseLoadTarget(agreement.loadTo);
  if (target?.kind === 'account') {
    set('R', target.code);
    set('S', 'Y');
  } else if (target?.kind === 'group') {
    set('T', target.code);
    set('U', 'Y');
  }
  set('V', agreement.oldSpa);
  set('W', 'Y');
  set('X', 'MASS LOAD');
  set('Y', 'Y');
  set('Z', agreement.newSpa);
  set('AA', 'Y');
  set('AG', 'N');
  return cells;
}

const HUNDRED = new Decimal(100);

function priced(list: Decimal, rate: Decimal, decimals: number): Decimal {
  return list
    .times(HUNDRED.plus(rate))
    .dividedBy(HUNDRED)
    .toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP);
}

/**
 * The template's margin check on its rounded V and W: (V − W) / V ≤ 0, or no
 * sell price at all, leaves the item out.
 */
function hasMargin(sell: Decimal, cost: Decimal): boolean {
  return sell.greaterThan(0) && sell.greaterThan(cost);
}

/** The upload for one agreement, priced from one price book. */
export function buildCorporateUpload(input: {
  agreement: CorporateAgreement;
  /** Whether the SAPS workbook counts dates from 1904. */
  sapsDate1904: boolean;
  priceBook: ResultsSheet;
}): CorporateUploadResult {
  const { agreement, priceBook } = input;
  const resolved = resolveHeaders(priceBook, PRICE_BOOK_HEADERS);
  if (!('columns' in resolved)) {
    return {
      status: 'invalid',
      problems: [
        {
          kind: 'missing-headers',
          file: 'priceBook',
          headers: resolved.missing,
        },
      ],
    };
  }
  const { columns } = resolved;
  const header = headerCells(agreement, input.sapsDate1904);
  const lines: UploadLine[] = [];
  let unmatched = 0;
  let unpriced = 0;
  const leftOut: LeftOutItem[] = [];

  priceBook.rows.forEach((row, index) => {
    if (isBlankRow(row)) return;
    const rates =
      agreement.catalogRules.get(ruleKey(row[columns('catalog')])) ??
      agreement.priceGroupRules.get(ruleKey(row[columns('priceGroup')]));
    if (!rates) {
      unmatched += 1;
      return;
    }
    const listCell = row[columns('listPrice')];
    const list = cellDecimal(listCell);
    if (!list) {
      unpriced += 1;
      return;
    }
    const sheetRow = sheetRowOf(priceBook, index);
    const catalog = cellText(row[columns('catalog')]);
    const itemCell = row[columns('item')];
    const item = cellText(itemCell);
    // AS400 rejects a whole upload holding a `#N/A` (pricing team, Oct
    // 2026), so the item is left out and listed for fixing the price book.
    if (item === '' || isErrorCell(itemCell)) {
      leftOut.push({
        sheetRow,
        catalog,
        reason: 'no-item',
        sell: '',
        cost: '',
      });
      return;
    }
    const sell = priced(list, rates.customerRate, 2);
    const cost = priced(list, rates.rate, 4);
    const sellText = sell.toFixed(2);
    const costText = cost.toFixed(4);
    if (!hasMargin(sell, cost)) {
      leftOut.push({
        sheetRow,
        catalog,
        reason: 'margin',
        sell: sellText,
        cost: costText,
      });
      return;
    }
    const cells = [...header];
    cells[columnIndex('AL')] = item;
    cells[columnIndex('AQ')] = sellText;
    cells[columnIndex('AY')] = costText;
    // Last guard: no Excel error in any column (part number, sell, cost or a
    // SAPS field) ever reaches AS400.
    if (cells.some(isExcelErrorText)) {
      leftOut.push({
        sheetRow,
        catalog,
        reason: 'error-value',
        sell: sellText,
        cost: costText,
      });
      return;
    }
    lines.push({ sheetRow, cells });
  });

  return { status: 'ok', lines, unmatched, unpriced, leftOut };
}

export interface CorporateSelection {
  /** Null picks the first division. */
  division: string | null;
}

/** What one load contributed to the upload. */
export interface LoadSummary {
  load: CorporateLoad;
  /** The price book its Fixed val.date picked. */
  priceBookName: string;
  /** Its LOAD NUM: 1 for the file's first Cust Name, 2 for the next… */
  loadNumber: number;
  lines: number;
  /** Price-book items no rule of this load prices. */
  unmatched: number;
  /** Items a rule prices whose list price is not a number: left out. */
  unpriced: number;
  noItem: number;
  lowMargin: number;
}

/** A load of the division no dropped price book covers: not in the file. */
export interface UnpricedLoad {
  load: CorporateLoad;
  /** Its `Fixed val.date`. */
  pricingDay: Date | null;
}

export interface LoadProblem {
  load: CorporateLoad;
  problem: CorporateProblem;
}

export type LeftOutRow = LeftOutItem & {
  load: CorporateLoad;
  /** The book `sheetRow` is a row of. */
  priceBookName: string;
};

export type DivisionUpload =
  | {
      status: 'ok';
      csv: string;
      lines: UploadLine[];
      loads: LoadSummary[];
      leftOut: LeftOutRow[];
      /** The division's loads whose price book was not dropped. */
      unpricedLoads: UnpricedLoad[];
    }
  | { status: 'invalid'; problems: LoadProblem[] };

export interface CorporateView {
  divisions: string[];
  division: string | null;
  /** Dropped price books whose file name carries no dates: never used. */
  undatedPriceBooks: string[];
  /** Null until a SAPS file with a named load and a price book are in hand. */
  result: DivisionUpload | null;
}

type PriceBookFile = { fileName: string; sheet: ResultsSheet };

interface ReadLoad {
  load: CorporateLoad;
  agreement: CorporateAgreement;
  pricingDay: Date | null;
}

/** Every load's agreement, or every problem found reading them. */
function readLoads(
  saps: ResultsSheet,
  loads: CorporateLoad[],
): { read: ReadLoad[] } | { problems: LoadProblem[] } {
  const date1904 = saps.date1904 ?? false;
  const read: ReadLoad[] = [];
  const problems: LoadProblem[] = [];
  for (const load of loads) {
    const result = readCorporateAgreement(saps, load);
    if ('problems' in result) {
      problems.push(...result.problems.map((problem) => ({ load, problem })));
    } else {
      read.push({
        load,
        agreement: result.agreement,
        pricingDay: agreementPricingDay(result.agreement, date1904),
      });
    }
  }
  return problems.length > 0 ? { problems } : { read };
}

/** LOAD NUM: 1 for every line of the first Cust Name, 2 for the next… */
function loadNumbers(loads: CorporateLoad[]): Map<string, number> {
  const numbers = new Map<string, number>();
  for (const { customer } of loads) {
    if (!numbers.has(customer)) numbers.set(customer, numbers.size + 1);
  }
  return numbers;
}

function summarize(
  load: CorporateLoad,
  priceBookName: string,
  loadNumber: number,
  result: Extract<CorporateUploadResult, { status: 'ok' }>,
): LoadSummary {
  const count = (reason: LeftOutReason) =>
    result.leftOut.filter((item) => item.reason === reason).length;
  return {
    load,
    priceBookName,
    loadNumber,
    lines: result.lines.length,
    unmatched: result.unmatched,
    unpriced: result.unpriced,
    noItem: count('no-item'),
    lowMargin: count('margin'),
  };
}

/** A day as the SAPS shows it: MM/DD/YYYY. */
export function sapsDayText(day: Date): string {
  const part = (value: number) => String(value).padStart(2, '0');
  return `${part(day.getUTCMonth() + 1)}/${part(day.getUTCDate())}/${day.getUTCFullYear()}`;
}

type BookChoice =
  | { kind: 'book'; priceBook: PriceBookFile }
  | { kind: 'none' }
  | { kind: 'ambiguous'; problem: CorporateProblem };

/** The one dated price book covering the SPA's Fixed val.date. */
function bookFor(day: Date | null, priceBooks: PriceBookFile[]): BookChoice {
  const covering = day
    ? priceBooks.filter(({ fileName }) => priceBookCovers(fileName, day))
    : [];
  const [only, ...more] = covering;
  if (!only || !day) return { kind: 'none' };
  if (more.length === 0) return { kind: 'book', priceBook: only };
  return {
    kind: 'ambiguous',
    problem: {
      kind: 'ambiguous-price-book',
      day: sapsDayText(day),
      priceBookNames: covering.map(({ fileName }) => fileName),
    },
  };
}

/** Each load with the book that prices it, the loads with none, or problems. */
function assignBooks(
  read: ReadLoad[],
  priceBooks: PriceBookFile[],
):
  | {
      priced: (ReadLoad & { priceBook: PriceBookFile })[];
      unpricedLoads: UnpricedLoad[];
    }
  | { problems: LoadProblem[] } {
  const priced: (ReadLoad & { priceBook: PriceBookFile })[] = [];
  const unpricedLoads: UnpricedLoad[] = [];
  const problems: LoadProblem[] = [];
  for (const entry of read) {
    const choice = bookFor(entry.pricingDay, priceBooks);
    if (choice.kind === 'book') priced.push({ ...entry, ...choice });
    else if (choice.kind === 'ambiguous') {
      problems.push({ load: entry.load, problem: choice.problem });
    } else {
      unpricedLoads.push({ load: entry.load, pricingDay: entry.pricingDay });
    }
  }
  return problems.length > 0 ? { problems } : { priced, unpricedLoads };
}

/**
 * One division's upload: every load of the division, in SAPS order, each
 * priced from the dropped book covering its `Fixed val.date`. A load whose
 * book was not dropped is named and left out; the rest still upload.
 */
export function buildDivisionUpload(input: {
  saps: ResultsSheet;
  loads: CorporateLoad[];
  priceBooks: PriceBookFile[];
}): DivisionUpload {
  const readResult = readLoads(input.saps, input.loads);
  if ('problems' in readResult) {
    return { status: 'invalid', problems: readResult.problems };
  }
  const assigned = assignBooks(readResult.read, input.priceBooks);
  if ('problems' in assigned) {
    return { status: 'invalid', problems: assigned.problems };
  }
  const numbers = loadNumbers(assigned.priced.map(({ load }) => load));
  const loads: LoadSummary[] = [];
  const lines: UploadLine[] = [];
  const leftOut: LeftOutRow[] = [];
  for (const { load, agreement, priceBook } of assigned.priced) {
    const result = buildCorporateUpload({
      agreement,
      sapsDate1904: input.saps.date1904 ?? false,
      priceBook: priceBook.sheet,
    });
    if (result.status === 'invalid') {
      return {
        status: 'invalid',
        problems: result.problems.map((problem) => ({ load, problem })),
      };
    }
    const loadNumber = numbers.get(load.customer) ?? 0;
    for (const line of result.lines) {
      line.cells[columnIndex('A')] = String(loadNumber);
    }
    const priceBookName = priceBook.fileName;
    lines.push(...result.lines);
    leftOut.push(
      ...result.leftOut.map((item) => ({ ...item, load, priceBookName })),
    );
    loads.push(summarize(load, priceBookName, loadNumber, result));
  }
  return {
    status: 'ok',
    csv: toCsv(lines.map(({ cells }) => cells)),
    lines,
    loads,
    leftOut,
    unpricedLoads: assigned.unpricedLoads,
  };
}

/**
 * A cell Excel would run as a formula (`=`, `+`, `-`, `@`, tab, CR) gets a
 * leading `'`, so it shows as text. Only for the report people open in Excel;
 * UPLOAD.csv goes to AS400 as is.
 */
function spreadsheetSafe(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

const LEFT_OUT_REASON_TEXT: Record<LeftOutReason, string> = {
  'no-item': 'No AS400 item',
  margin: 'Margin 0 or less',
  'error-value': 'Excel error value',
};

/**
 * The left-out items as a CSV with a header row, for fixing the price book:
 * the on-screen list shows only the first few hundred.
 */
export function leftOutItemsCsv(items: LeftOutRow[]): string {
  return toCsv([
    [
      'Cust Name',
      'LOADED TO',
      'Branch',
      'Price book',
      'Price book row',
      'Catalog Number',
      'Reason',
      'Sell',
      'Cost',
    ],
    ...items.map(
      ({ load, priceBookName, sheetRow, catalog, reason, sell, cost }) =>
        [
          load.customer,
          load.loadTo,
          // The branch (W+), not the bare `+` a spreadsheet would read as math.
          `W${load.division}`,
          priceBookName,
          String(sheetRow),
          catalog,
          LEFT_OUT_REASON_TEXT[reason],
          sell,
          cost,
        ].map(spreadsheetSafe),
    ),
  ]);
}

/**
 * Everything the corporate panel shows, from the dropped files and the user's
 * pick of division. Pure, so any change re-derives it.
 */
export function deriveCorporateUpload(input: {
  saps: ResultsSheet | null;
  priceBooks: PriceBookFile[];
  selection: CorporateSelection;
}): CorporateView {
  const { saps, priceBooks, selection } = input;
  const loads = saps ? corporateLoads(saps) : [];
  const divisions = corporateDivisions(loads);
  const division =
    divisions.find((code) => code === selection.division) ??
    divisions[0] ??
    null;
  const undatedPriceBooks = priceBooks.flatMap(({ fileName }) =>
    priceBookWindow(fileName) ? [] : [fileName],
  );
  const view: CorporateView = {
    divisions,
    division,
    undatedPriceBooks,
    result: null,
  };
  if (!saps || division === null || priceBooks.length === 0) return view;
  return {
    ...view,
    result: buildDivisionUpload({
      saps,
      loads: loads.filter((load) => load.division === division),
      priceBooks,
    }),
  };
}
